"use client";

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { MonthlyProjectionPoint } from "@/lib/do-or-dump-projections";

type Metric = "demand" | "revenue" | "costs";

const METRIC_ROWS: { id: Metric; label: string; hint: string }[] = [
  { id: "demand", label: "Demand", hint: "units/month (left axis when layered)" },
  { id: "revenue", label: "Revenue", hint: "dollars/month" },
  { id: "costs", label: "Costs", hint: "dollars/month" },
];

const METRIC_VISUAL: Record<
  Metric,
  { stroke: string; stopStrong: string; stopFade: string; dotGlow: string }
> = {
  demand: {
    stroke: "#6366F1",
    stopStrong: "rgba(99, 102, 241, 0.38)",
    stopFade: "rgba(99, 102, 241, 0)",
    dotGlow: "rgba(99, 102, 241, 0.35)",
  },
  revenue: {
    stroke: "#00C853",
    stopStrong: "rgba(0, 200, 83, 0.36)",
    stopFade: "rgba(0, 200, 83, 0)",
    dotGlow: "rgba(0, 200, 83, 0.4)",
  },
  costs: {
    stroke: "#FF5A5F",
    stopStrong: "rgba(255, 90, 95, 0.32)",
    stopFade: "rgba(255, 90, 95, 0)",
    dotGlow: "rgba(255, 90, 95, 0.35)",
  },
};

function pickValue(p: MonthlyProjectionPoint, m: Metric): number {
  if (m === "demand") return p.demandUnits;
  if (m === "revenue") return p.revenue;
  return p.costs;
}

function formatAxisTick(n: number, kind: "demand" | "dollar"): string {
  if (kind === "demand") {
    if (n >= 1_000_000) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 10_000) return `${Math.round(n / 1000)}k`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return `${Math.round(n)}`;
  }
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 100_000) return `$${Math.round(n / 1000)}k`;
  if (abs >= 1000) return `$${(n / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
}

function smoothLinePath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

type LayerState = Record<Metric, boolean>;

export function ProjectionChart({ points }: { points: MonthlyProjectionPoint[] }) {
  const reactId = useId().replace(/:/g, "");
  /** Revenue + costs on by default — demand optional overlay with left axis. */
  const [layered, setLayered] = useState<LayerState>({
    demand: false,
    revenue: true,
    costs: true,
  });

  function toggleMetric(m: Metric) {
    setLayered((prev) => {
      const next = { ...prev, [m]: !prev[m] };
      if (!next.demand && !next.revenue && !next.costs) return prev;
      return next;
    });
  }

  const chart = useMemo(() => {
    const W = 960;
    const H = 256;
    const showDemand = layered.demand;
    const showRev = layered.revenue;
    const showCost = layered.costs;
    const showDollar = showRev || showCost;
    const dualAxis = showDemand && showDollar;

    /** Space between chart frame and plot / labels — fixes cramped left edge when only $ series show. */
    const SIDE_GUTTER = 36;
    const LEFT_AXIS_BAND = 48;
    const RIGHT_AXIS_BAND = 52;

    const padBase = { top: 40, bottom: 54, innerTop: 40 };
    const padLeft = showDemand ? SIDE_GUTTER + LEFT_AXIS_BAND : SIDE_GUTTER;
    const padRight = showDollar ? SIDE_GUTTER + RIGHT_AXIS_BAND : SIDE_GUTTER;
    const pad = { top: padBase.top, right: padRight, bottom: padBase.bottom, left: padLeft };
    const innerW = W - pad.left - pad.right;
    const innerH = H - pad.top - pad.bottom;
    const bottomY = pad.top + innerH;

    const leftTickX = showDemand ? SIDE_GUTTER + 8 : 0;
    const rightTickX = W - SIDE_GUTTER - 10;
    const rightAxisTitleX = W - SIDE_GUTTER - 10;

    const empty = {
      W,
      H,
      pad,
      innerW,
      innerH,
      bottomY,
      dualAxis,
      showDemand,
      showDollar,
      leftTickX,
      rightTickX,
      rightAxisTitleX,
      gridYs: [] as number[],
      leftTicks: [] as { y: number; label: string }[],
      rightTicks: [] as { y: number; label: string }[],
      series: [] as Array<{
        metric: Metric;
        linePath: string;
        areaPath: string | null;
      }>,
      activeSingleFill: null as Metric | null,
      snapPoints: [] as Array<{ x: number; demandY: number; revenueY: number; costsY: number }>,
    };

    if (points.length === 0) return empty;

    let demandRawMax = 1;
    let dollarRawMax = 1;
    for (const p of points) {
      if (showDemand) demandRawMax = Math.max(demandRawMax, p.demandUnits);
      if (showRev) dollarRawMax = Math.max(dollarRawMax, p.revenue);
      if (showCost) dollarRawMax = Math.max(dollarRawMax, p.costs);
    }
    const demandMax = demandRawMax * 1.08;
    const dollarMax = dollarRawMax * 1.08;

    const demandY = (v: number) => pad.top + innerH - (v / demandMax) * innerH;
    const dollarY = (v: number) => pad.top + innerH - (v / dollarMax) * innerH;

    const xAt = (i: number) => pad.left + (i / Math.max(1, points.length - 1)) * innerW;

    const divisions = 5;
    const gridYs: number[] = [];
    const leftTicks: { y: number; label: string }[] = [];
    const rightTicks: { y: number; label: string }[] = [];
    const labelSteps = new Set([0, 2, 4, 5]);

    for (let g = 0; g <= divisions; g++) {
      const t = g / divisions;
      const y = pad.top + t * innerH;
      gridYs.push(y);

      if (!labelSteps.has(g)) continue;

      if (dualAxis) {
        const demandVal = demandMax * (1 - t);
        const dollarVal = dollarMax * (1 - t);
        leftTicks.push({ y: y + 3, label: formatAxisTick(demandVal, "demand") });
        rightTicks.push({ y: y + 3, label: formatAxisTick(dollarVal, "dollar") });
      } else if (showDemand && !showDollar) {
        leftTicks.push({ y: y + 3, label: formatAxisTick(demandMax * (1 - t), "demand") });
      } else if (showDollar) {
        rightTicks.push({ y: y + 3, label: formatAxisTick(dollarMax * (1 - t), "dollar") });
      }
    }

    const visibleMetrics: Metric[] = [];
    if (showCost) visibleMetrics.push("costs");
    if (showRev) visibleMetrics.push("revenue");
    if (showDemand) visibleMetrics.push("demand");

    const activeCount = visibleMetrics.length;
    const activeSingleFill = activeCount === 1 ? visibleMetrics[0] : null;

    const snapPoints = points.map((p, i) => ({
      x: xAt(i),
      demandY: demandY(p.demandUnits),
      revenueY: dollarY(p.revenue),
      costsY: dollarY(p.costs),
    }));

    const series = visibleMetrics.map((metric) => {
      const vals = points.map((p) => pickValue(p, metric));
      const yScale = metric === "demand" ? demandY : dollarY;
      const pts = points.map((_, i) => ({
        x: xAt(i),
        y: yScale(vals[i]),
      }));
      const linePath = smoothLinePath(pts);
      const last = pts[pts.length - 1];
      const first = pts[0];
      let areaPath: string | null = null;
      if (activeSingleFill === metric && pts.length > 0) {
        areaPath = `${linePath} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
      }
      return {
        metric,
        linePath,
        areaPath,
      };
    });

    return {
      W,
      H,
      pad,
      innerW,
      innerH,
      bottomY,
      dualAxis,
      showDemand,
      showDollar,
      leftTickX,
      rightTickX,
      rightAxisTitleX,
      gridYs,
      leftTicks,
      rightTicks,
      series,
      activeSingleFill,
      snapPoints,
    };
  }, [points, layered]);

  const glowId = `proj-glow-${reactId}`;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const nearestIndexFromSvgX = useCallback((svgX: number): number | null => {
    const xs = chart.snapPoints.map((s) => s.x);
    if (xs.length === 0) return null;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < xs.length; i++) {
      const d = Math.abs(xs[i] - svgX);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }, [chart.snapPoints]);

  const updateHoverFromClient = useCallback(
    (clientX: number, bounds: DOMRect) => {
      const frac = (clientX - bounds.left) / bounds.width;
      const svgX = frac * chart.W;
      setHoverIndex(nearestIndexFromSvgX(svgX));
    },
    [chart.W, nearestIndexFromSvgX],
  );

  useEffect(() => {
    setHoverIndex(null);
  }, [layered]);

  function yForMetric(metric: Metric, snap: (typeof chart.snapPoints)[0]): number {
    if (metric === "demand") return snap.demandY;
    if (metric === "revenue") return snap.revenueY;
    return snap.costsY;
  }

  const hoverSnap =
    hoverIndex !== null && chart.snapPoints[hoverIndex] ? chart.snapPoints[hoverIndex] : null;
  const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div className="rounded-3xl border border-zinc-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">12‑month outlook</h3>
          <p className="mt-1.5 text-sm leading-snug text-zinc-600">
            Turn layers on or off to compare revenue, costs, and optional demand (units per month).
          </p>
        </div>
        <fieldset className="flex shrink-0 flex-wrap gap-2 border-0 p-0 lg:max-w-md lg:justify-end">
          <legend className="sr-only">Series to show</legend>
          {METRIC_ROWS.map((row) => {
            const on = layered[row.id];
            const color = METRIC_VISUAL[row.id].stroke;
            return (
              <label
                key={row.id}
                className={`flex cursor-pointer items-center gap-2 rounded-2xl border bg-white px-3 py-2 text-xs font-semibold text-zinc-800 shadow-sm transition sm:text-[13px] ${
                  on ? "border-zinc-400 ring-1 ring-zinc-200" : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/80"
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleMetric(row.id)}
                  className="peer sr-only"
                  aria-describedby={`hint-${row.id}`}
                />
                <span
                  className={`flex size-5 shrink-0 items-center justify-center rounded-md border-2 bg-white transition ${
                    on ? "border-zinc-500" : "border-zinc-300"
                  }`}
                  aria-hidden
                >
                  {on ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-zinc-900">
                      <path
                        d="M2.5 6.2 5.1 8.8 9.8 3.6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                  <span>{row.label}</span>
                </span>
                <span id={`hint-${row.id}`} className="sr-only">
                  {row.hint}
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>

      <div className="relative mt-5 w-full overflow-visible rounded-2xl bg-[#FAFAFA] ring-1 ring-zinc-200/60">
        <div className="relative w-full" style={{ aspectRatio: `${chart.W} / ${chart.H}` }}>
          <svg
            viewBox={`0 0 ${chart.W} ${chart.H}`}
            className="absolute inset-0 h-full w-full select-none"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden
          >
            <defs>
              <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {(["demand", "revenue", "costs"] as const).map((m) => (
                <linearGradient key={m} id={`proj-fill-${reactId}-${m}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={METRIC_VISUAL[m].stopStrong} />
                  <stop offset="55%" stopColor={METRIC_VISUAL[m].stopFade} />
                  <stop offset="100%" stopColor={METRIC_VISUAL[m].stopFade} />
                </linearGradient>
              ))}
            </defs>

            <rect x="0" y="0" width={chart.W} height={chart.H} fill="#FAFAFA" rx="14" />

            {chart.gridYs.map((gy, i) => (
              <line
                key={i}
                x1={chart.pad.left}
                y1={gy}
                x2={chart.W - chart.pad.right}
                y2={gy}
                stroke="#E4E4E7"
                strokeWidth="1"
                strokeDasharray={chart.dualAxis ? "3 6" : i === chart.gridYs.length - 1 ? "0" : "4 6"}
                opacity={chart.dualAxis ? 0.45 : i === chart.gridYs.length - 1 ? 0.45 : 0.65}
              />
            ))}

            {chart.leftTicks.length > 0 ? (
              <text
                x={chart.leftTickX}
                y={26}
                textAnchor="start"
                className="fill-zinc-400"
                style={{ fontSize: 10, fontWeight: 700 }}
              >
                {chart.dualAxis ? "Units (left)" : "Units"}
              </text>
            ) : null}
            {chart.leftTicks.map((tick, i) => (
              <text
                key={`L-${i}`}
                x={chart.leftTickX}
                y={tick.y}
                textAnchor="start"
                className="fill-zinc-500"
                style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {tick.label}
              </text>
            ))}

            {chart.rightTicks.length > 0 ? (
              <text
                x={chart.rightAxisTitleX}
                y={26}
                textAnchor="end"
                className="fill-zinc-400"
                style={{ fontSize: 10, fontWeight: 700 }}
              >
                {chart.dualAxis ? "$ (right)" : "$"}
              </text>
            ) : null}
            {chart.rightTicks.map((tick, i) => (
              <text
                key={`R-${i}`}
                x={chart.rightTickX}
                y={tick.y}
                textAnchor="end"
                className="fill-zinc-500"
                style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {tick.label}
              </text>
            ))}

            {chart.series.map((s) =>
              s.areaPath ? (
                <path
                  key={`a-${s.metric}`}
                  d={s.areaPath}
                  fill={`url(#proj-fill-${reactId}-${s.metric})`}
                  stroke="none"
                  opacity={0.92}
                />
              ) : null,
            )}

            {chart.series.map((s) => (
              <path
                key={`l-${s.metric}`}
                d={s.linePath}
                fill="none"
                stroke={METRIC_VISUAL[s.metric].stroke}
                strokeWidth={chart.series.length > 1 ? 2 : 2.35}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#${glowId})`}
              />
            ))}

            {hoverSnap ? (
              <line
                x1={hoverSnap.x}
                y1={chart.pad.top}
                x2={hoverSnap.x}
                y2={chart.bottomY}
                stroke="#71717A"
                strokeWidth={1}
                strokeDasharray="5 5"
                opacity={0.85}
                pointerEvents="none"
              />
            ) : null}

            {hoverSnap
              ? chart.series.map((s) => {
                  const cy = yForMetric(s.metric, hoverSnap);
                  const cx = hoverSnap.x;
                  return (
                    <g key={`h-${s.metric}`} pointerEvents="none">
                      <circle cx={cx} cy={cy} r={8} fill={METRIC_VISUAL[s.metric].dotGlow} />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill={METRIC_VISUAL[s.metric].stroke}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    </g>
                  );
                })
              : null}

            {points.map((p, i) => {
              const x = chart.pad.left + (i / Math.max(1, points.length - 1)) * chart.innerW;
              return (
                <text
                  key={p.monthIndex}
                  x={x}
                  y={chart.H - 16}
                  textAnchor="middle"
                  className="fill-zinc-400"
                  style={{ fontSize: 10, fontWeight: 600 }}
                >
                  {p.label}
                </text>
              );
            })}
          </svg>

          {hoverSnap && hoverPoint ? (
            <div
              className="pointer-events-none absolute z-10 min-w-[10rem] max-w-[min(17rem,calc(100%-1rem))] rounded-xl border border-zinc-200/90 bg-white px-3 py-2 shadow-lg shadow-zinc-900/10"
              style={{
                left: `${(hoverSnap.x / chart.W) * 100}%`,
                top: 8,
                transform: "translateX(-50%)",
              }}
            >
              <p className="text-[11px] font-bold text-zinc-900">{hoverPoint.label}</p>
              <ul className="mt-1.5 space-y-1 text-[11px] font-semibold tabular-nums">
                {layered.revenue ? (
                  <li className="flex justify-between gap-4">
                    <span style={{ color: METRIC_VISUAL.revenue.stroke }}>Revenue</span>
                    <span className="text-zinc-900">{formatAxisTick(hoverPoint.revenue, "dollar")}</span>
                  </li>
                ) : null}
                {layered.costs ? (
                  <li className="flex justify-between gap-4">
                    <span style={{ color: METRIC_VISUAL.costs.stroke }}>Costs</span>
                    <span className="text-zinc-900">{formatAxisTick(hoverPoint.costs, "dollar")}</span>
                  </li>
                ) : null}
                {layered.demand ? (
                  <li className="flex justify-between gap-4">
                    <span style={{ color: METRIC_VISUAL.demand.stroke }}>Units</span>
                    <span className="text-zinc-900">{Math.round(hoverPoint.demandUnits).toLocaleString("en-US")}</span>
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}

          <div
            className="absolute inset-0 z-[5] cursor-crosshair"
            onMouseMove={(e) => updateHoverFromClient(e.clientX, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={() => setHoverIndex(null)}
            onTouchStart={(e) => {
              const t = e.touches[0];
              if (t) updateHoverFromClient(t.clientX, e.currentTarget.getBoundingClientRect());
            }}
            onTouchMove={(e) => {
              const t = e.touches[0];
              if (t) updateHoverFromClient(t.clientX, e.currentTarget.getBoundingClientRect());
            }}
            onTouchEnd={() => setHoverIndex(null)}
            aria-hidden
          />
        </div>
      </div>

      <p className="mt-3 text-center text-[11px] font-medium text-zinc-400">
        Hover or drag on the chart to see values by month.
      </p>
    </div>
  );
}
